import React, { useCallback, useEffect, useState } from 'react';

import { useCITranslate } from '../../i18n/customerIdentity';
import { CIConfirmPopup } from './CustomerIdentityShared';
import {
  deleteCustomerPaymentMethod,
  listCustomerPaymentMethods,
  updateCustomerPaymentMethod,
} from '../../services/customerIdentityApi';

const errorMessage = (err) => ({ kind: 'danger', error: err });
const successMessage = (i18nKey) => ({ kind: 'success', i18nKey });

export default function CustomerPaymentsTab({ customer, setMessage }) {
  const t = useCITranslate();

  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listCustomerPaymentMethods(customer.id, true);
      setMethods(data || []);
    } catch (err) {
      setMessage({
        kind: 'danger',
        i18nKey: 'payments.couldNotLoad',
        error: err,
      });
    } finally {
      setLoading(false);
    }
  }, [customer.id, setMessage]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSetDefault = async (pm) => {
    setBusy(true);
    try {
      await updateCustomerPaymentMethod(pm.id, { is_default: true });
      await load();
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleToggleActive = async (pm) => {
    setBusy(true);
    try {
      await updateCustomerPaymentMethod(pm.id, { is_active: !pm.is_active });
      await load();
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!pendingDelete) return;
    setBusy(true);
    try {
      await deleteCustomerPaymentMethod(pendingDelete.id);
      setPendingDelete(null);
      await load();
      setMessage(successMessage('payments.removed'));
    } catch (err) {
      setMessage(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h6 className="m-0">{t('payments.title')}</h6>
        <span className="text-muted small">{t('payments.securityNote')}</span>
      </div>
      <div className="table-responsive">
        <table className="table m-0">
          <thead>
            <tr>
              <th>{t('payments.columns.brandType')}</th>
              <th>{t('payments.columns.lastFour')}</th>
              <th>{t('payments.columns.expiry')}</th>
              <th>{t('payments.columns.gateway')}</th>
              <th>{t('common.status')}</th>
              <th className="text-end">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} className="text-muted py-3">
                  {t('common.loading')}
                </td>
              </tr>
            ) : null}
            {methods.length === 0 && !loading ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  {t('payments.empty')}
                </td>
              </tr>
            ) : null}
            {methods.map((pm) => (
              <tr key={pm.id}>
                <td>
                  <div>{pm.brand || pm.gateway}</div>
                  <div className="small text-muted text-capitalize">{pm.type}</div>
                </td>
                <td>•••• {pm.last_four || '----'}</td>
                <td>
                  {pm.expiry_month || '?'}/{pm.expiry_year || '?'}
                </td>
                <td>
                  <code style={{ fontSize: 11 }}>{pm.gateway_payment_method_id}</code>
                </td>
                <td>
                  {pm.is_default ? (
                    <span className="badge bg-primary me-1">
                      {t('payments.badges.default')}
                    </span>
                  ) : null}
                  <span
                    className={`badge ${pm.is_active ? 'bg-success' : 'bg-secondary'}`}
                  >
                    {pm.is_active
                      ? t('payments.badges.active')
                      : t('payments.badges.inactive')}
                  </span>
                </td>
                <td className="text-end">
                  <div className="btn-group btn-group-sm">
                    {!pm.is_default ? (
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => handleSetDefault(pm)}
                        disabled={busy}
                      >
                        {t('payments.actions.makeDefault')}
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() => handleToggleActive(pm)}
                      disabled={busy}
                    >
                      {pm.is_active
                        ? t('payments.actions.deactivate')
                        : t('payments.actions.activate')}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-danger"
                      onClick={() => setPendingDelete(pm)}
                    >
                      {t('common.delete')}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CIConfirmPopup
        open={Boolean(pendingDelete)}
        title={t('payments.deleteConfirmTitle')}
        message={t('payments.deleteConfirm')}
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        danger
        onConfirm={handleDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
